import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { useCarrierCallback } from '@/api/domains/carriers';
import { useAuth } from '@/auth/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toApiProblem } from '@/lib/errors';

type Phase = 'pending' | 'error';

const OAUTH_MESSAGE_TYPE = 'inpost-oauth-callback';

function isPopupWindow(): boolean {
  try {
    return !!window.opener && window.opener !== window;
  } catch {
    return false;
  }
}

export function InPostCallbackPage() {
  const { t } = useTranslation('carriers');
  const auth = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [phase, setPhase] = useState<Phase>('pending');
  const [message, setMessage] = useState<string>(t('connect.callback.inpost.pendingMessage'));
  const attempted = useRef(false);
  const callback = useCarrierCallback('inpost');

  useEffect(() => {
    if (attempted.current) return;

    const code = params.get('code');
    const state = params.get('state');
    const oauthError = params.get('error');
    const popup = isPopupWindow();

    if (popup) {
      attempted.current = true;
      try {
        window.opener?.postMessage(
          { type: OAUTH_MESSAGE_TYPE, code, state, error: oauthError },
          window.location.origin,
        );
      } catch {
        // ignore - opener might be cross-origin or already closed
      }
      window.close();
      return;
    }

    if (auth.status !== 'authenticated') return;
    attempted.current = true;

    if (oauthError) {
      setPhase('error');
      setMessage(t('connect.callback.inpost.rejected', { error: oauthError }));
      return;
    }

    if (!code || !state) {
      setPhase('error');
      setMessage(t('connect.callback.inpost.missingParams'));
      return;
    }

    callback.mutate(
      { code, state },
      {
        onSuccess: (result) => {
          if (result.needs_organization_selection) {
            toast.success(t('connect.callback.inpost.needsOrgToast'));
            navigate('/connect/inpost/select-organization', { replace: true });
          } else {
            toast.success(t('connect.callback.inpost.connectedToast'));
            navigate('/authorization', { replace: true });
          }
        },
        onError: (error) => {
          const problem = toApiProblem(error);
          setPhase('error');
          setMessage(problem.detail ?? problem.title ?? t('connect.callback.inpost.failed'));
        },
      },
    );
  }, [auth, params, navigate, callback, t]);

  return (
    <div className="flex min-h-full items-center justify-center bg-muted/40 p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('connect.callback.inpost.title')}</CardTitle>
          <CardDescription>
            {phase === 'pending'
              ? t('connect.callback.inpost.pending')
              : t('connect.callback.inpost.error')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <p
            className={
              phase === 'error' ? 'text-sm text-destructive' : 'text-sm text-muted-foreground'
            }
          >
            {message}
          </p>
          {phase === 'error' ? (
            <a
              href="/authorization"
              className="text-sm font-medium text-primary underline-offset-4 hover:underline"
            >
              {t('connect.callback.inpost.back')}
            </a>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
